import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import Pager from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDate, formatCountdown } from "@/lib/format";

const PAGE_SIZE = 20;

export default function Subscriptions() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("active");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [now, setNow] = useState(Date.now());

  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);
  useEffect(() => { const iv = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(iv); }, []);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/subscriptions", {
        params: { status, q: debouncedSearch || undefined, page, page_size: PAGE_SIZE },
      });
      setItems(data.items); setTotal(data.total);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [status, debouncedSearch, page]);

  const revoke = async (id) => {
    if (!confirm("Revoke this subscription?")) return;
    try {
      await api.post(`/admin/subscriptions/${id}/revoke`);
      toast.success("Revoked");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div>
      <PageHeader
        testId="subs-header"
        title="Subscriptions"
        subtitle="Active hotspot sessions and history"
        actions={
          <Tabs value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <TabsList className="bg-nhp-surface border border-nhp-border rounded-none h-10 overflow-x-auto max-w-full">
              <TabsTrigger data-testid="tab-all" value="all" className="rounded-none data-[state=active]:bg-nhp-primary data-[state=active]:text-nhp-on-primary">All</TabsTrigger>
              <TabsTrigger data-testid="tab-active" value="active" className="rounded-none data-[state=active]:bg-nhp-primary data-[state=active]:text-nhp-on-primary">Active</TabsTrigger>
              <TabsTrigger data-testid="tab-expired" value="expired" className="rounded-none data-[state=active]:bg-nhp-primary data-[state=active]:text-nhp-on-primary">Expired</TabsTrigger>
              <TabsTrigger data-testid="tab-revoked" value="revoked" className="rounded-none data-[state=active]:bg-nhp-primary data-[state=active]:text-nhp-on-primary">Revoked</TabsTrigger>
            </TabsList>
          </Tabs>
        }
      />

      <div className="p-4 md:p-8 space-y-4">
        <div className="relative max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-nhp-muted" />
          <Input
            data-testid="subs-search"
            placeholder="Search by phone or package..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-nhp-surface border-nhp-border text-nhp-text rounded-none h-10 font-mono"
          />
        </div>
        <div className="border border-nhp-border bg-nhp-surface">
          <div className="hidden md:grid grid-cols-[repeat(14,minmax(0,1fr))] px-6 py-3 border-b border-nhp-border text-[10px] uppercase tracking-[0.2em] text-nhp-muted font-mono">
            <div className="col-span-2">Phone</div>
            <div className="col-span-2">Package</div>
            <div className="col-span-2">Site</div>
            <div className="col-span-1">Devices</div>
            <div className="col-span-2">Started</div>
            <div className="col-span-2">Expires</div>
            <div className="col-span-1">Remaining</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          <div className="divide-y divide-nhp-border">
            {items.length === 0 && <div className="p-8 text-center text-sm text-nhp-muted">No subscriptions.</div>}
            {items.map((s) => {
              const remaining = new Date(s.expires_at).getTime() - now;
              // Only worth flagging while the subscription itself is still
              // active -- an expired/revoked one not being bypassed is
              // expected, not a problem.
              const bypassMismatch = s.status === "active" && s.current_site && s.currently_bypassed === false;
              return (
                <div key={s.id} data-testid={`sub-row-${s.id}`} className="flex flex-col gap-2 md:grid md:grid-cols-[repeat(14,minmax(0,1fr))] px-4 md:px-6 py-4 md:items-center hover:bg-nhp-bg">
                  <div className="flex items-center justify-between md:contents">
                    <div className="font-mono md:col-span-2">{s.customer_phone}</div>
                    <div className="font-mono tabular-nums font-bold md:col-span-1 md:order-7">
                      {s.status === "active" && remaining > 0 ? (
                        <span className="text-nhp-primary">{formatCountdown(remaining)}</span>
                      ) : (
                        <span className={s.status === "revoked" ? "text-nhp-critical" : "text-nhp-muted"}>{s.status.toUpperCase()}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-nhp-muted md:contents">
                    <div className="md:col-span-2 text-nhp-text">{s.package_name}</div>
                    <div className="font-mono text-xs md:col-span-2">
                      {s.current_site ? (
                        <>
                          <div className="text-nhp-text">{s.current_site}</div>
                          <div className={`text-[10px] ${bypassMismatch ? "text-nhp-warning" : "text-nhp-muted"}`}>
                            {s.current_router}{bypassMismatch ? " · not bypassed" : ""}
                          </div>
                        </>
                      ) : (
                        <span className="text-nhp-muted text-[10px] uppercase tracking-[0.15em]">Not seen</span>
                      )}
                    </div>
                    <div className="font-mono tabular-nums md:col-span-1">
                      <span className={s.connected_devices >= s.simultaneous_devices ? "text-nhp-warning" : "text-nhp-muted"}>
                        {s.connected_devices ?? 0}/{s.simultaneous_devices} devices
                      </span>
                    </div>
                    <div className="font-mono text-xs md:col-span-2">
                      <span className="md:hidden">Started </span>{formatDate(s.started_at)}
                    </div>
                    <div className="font-mono text-xs md:col-span-2">
                      <span className="md:hidden">Expires </span>{formatDate(s.expires_at)}
                    </div>
                  </div>
                  {s.status === "active" && (
                    <div className="md:col-span-2 md:order-8 md:text-right">
                      <Button size="sm" variant="ghost" onClick={() => revoke(s.id)} className="rounded-none text-nhp-critical hover:text-nhp-text hover:bg-nhp-critical font-mono text-xs h-8">
                        REVOKE
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <Pager page={page} total={total} pageSize={PAGE_SIZE} itemLabel="subscriptions" onChange={setPage} testId="subscriptions" />
        </div>
      </div>
    </div>
  );
}
